/**
 * API 配置
 * @description 配置 axios 实例和请求拦截器
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useToast } from "@/components/ui/toast/use-toast"
const { toast } = useToast()
import { BASE_URL } from './index'

// API 基础URL配置
const API_BASE_URL = BASE_URL

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 增加到120秒，因为权限同步可能需要较长时间
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 可以在这里添加 token
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.config.url}`, response.data)
    
    // 统一处理响应格式
    // 后端返回格式是 { code, message, data }
    const responseData = response.data
    
    // 检查是否是标准格式
    if (responseData.code !== undefined) {
      const { code, message, data } = responseData
      if (code === 200) {
        // 直接返回 data，保持与现有代码兼容
        return data
      } else {
        // 未配置的页面内容：后端可能仍返回业务码 404，统一视为「无数据」
        if (code === 404) {
          return data ?? null
        }
        console.error('[API Error]', message)
        // 将完整的响应对象附加到错误上，方便前端提取数据
        const error = new Error(message || '请求失败')
        ;(error as any).response = response
        return Promise.reject(error)
      }
    } else if (responseData.success !== undefined) {
      // 另一种格式 { success, message, data }
      if (responseData.success) {
        return responseData.data || responseData
      } else {
        console.error('[API Error]', responseData.message)
        return Promise.reject(new Error(responseData.message || '请求失败'))
      }
    } else {
      // 直接返回数据
      return responseData
    }
  },
  (error: AxiosError) => {
    console.error('[API Response Error]', error)
    
    // 处理 HTTP 错误
    let errorMessage = '网络错误，请稍后重试'
    
    if (error.response) {
      const { status } = error.response
      
      switch (status) {
        case 400:
          // 尝试从响应中获取详细的错误信息
          const responseData = error.response.data as any
          errorMessage = responseData?.message || responseData?.msg || responseData?.error || '请求参数错误'
          break
        case 401:
          errorMessage = '未授权，请登录'
          // 清除 token 并跳转到登录页
          localStorage.removeItem('token')
          // 检查当前路径是否是登录页（考虑 base path）
          const currentPath = window.location.pathname
          const loginPath = '/admin/login'
          if (currentPath !== loginPath && !currentPath.endsWith('/login')) {
            window.location.href = loginPath
          }
          break
        case 403:
          errorMessage = '没有权限访问'
          break
        case 404:
          errorMessage = '请求的资源不存在'
          break
        case 500:
          errorMessage = '服务器内部错误'
          break
        default:
          errorMessage = (error.response.data as any)?.message || (error.response.data as any)?.msg || errorMessage
      }
    } else if (error.request) {
      errorMessage = '网络连接失败，请检查网络'
    }
    
    return Promise.reject(new Error(errorMessage))
  }
)

export default apiClient
export { API_BASE_URL }

